/**
 * Lane layout for a single day's timed events.
 *
 * Given a list of events with `startMin` / `endMin` (minutes from midnight),
 * assign each event a `laneIndex` and a cluster-wide `laneCount` so the
 * calling render code can lay them out side-by-side instead of stacking
 * them on top of one another.
 *
 * Algorithm (sweep-line):
 *   1. Sort by `startMin` asc, tiebreak by `endMin` desc so longer events
 *      open their lane first.
 *   2. Walk events, grouping into clusters. A new cluster starts when
 *      the current event begins at or after the running max `endMin`
 *      of the open cluster.
 *   3. Within a cluster, assign each event a fresh `laneIndex` equal to
 *      its position in the cluster. Lanes are NOT reused mid-cluster --
 *      this keeps pill widths visually consistent when the chain is
 *      a-b-c with only a-b and b-c overlapping (all three render in a
 *      3-column cluster instead of `c` suddenly stretching back to
 *      lane 0's full width).
 *   4. After the cluster closes, every event in it is stamped with the
 *      cluster's `laneCount = maxLaneIndex + 1` so their columns align.
 *
 * Back-to-back events (prev.end === next.start) are treated as
 * non-overlapping -- they share a lane.
 *
 * TODO: when a cluster's `laneCount` exceeds ~4 this makes pills very
 * narrow. A follow-up can clip to 4 visible lanes with a "+N more"
 * affordance; the first pass renders up to ~6 lanes cleanly.
 */

export interface LayoutInput {
  id: string | number;
  startMin: number;
  endMin: number;
}

export interface LayoutResult {
  id: string | number;
  laneIndex: number;
  laneCount: number;
}

export function layoutDayEvents<T extends LayoutInput>(
  events: readonly T[],
): LayoutResult[] {
  if (events.length === 0) return [];

  const sorted = [...events].sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    return b.endMin - a.endMin;
  });

  type WorkingEntry = {
    input: T;
    laneIndex: number;
    clusterId: number;
  };

  const entries: WorkingEntry[] = [];

  let clusterId = -1;
  let clusterEnd = -Infinity;
  let laneInCluster = 0;
  const clusterMax: number[] = [];

  for (const ev of sorted) {
    if (ev.startMin >= clusterEnd) {
      // Close previous cluster, open a new one.
      if (clusterId >= 0) clusterMax[clusterId] = laneInCluster - 1;
      clusterId += 1;
      clusterMax.push(0);
      clusterEnd = ev.endMin;
      laneInCluster = 0;
    } else {
      clusterEnd = Math.max(clusterEnd, ev.endMin);
    }

    entries.push({ input: ev, laneIndex: laneInCluster, clusterId });
    laneInCluster += 1;
  }
  if (clusterId >= 0) clusterMax[clusterId] = laneInCluster - 1;

  return entries.map((e) => ({
    id: e.input.id,
    laneIndex: e.laneIndex,
    laneCount: clusterMax[e.clusterId] + 1,
  }));
}

/**
 * Convenience: build a Map<id, {laneIndex, laneCount}> for O(1) lookup
 * while rendering. Keys match the `id` type of the input.
 */
export function buildLayoutMap<T extends LayoutInput>(
  events: readonly T[],
): Map<T["id"], { laneIndex: number; laneCount: number }> {
  const map = new Map<T["id"], { laneIndex: number; laneCount: number }>();
  for (const r of layoutDayEvents(events)) {
    map.set(r.id as T["id"], { laneIndex: r.laneIndex, laneCount: r.laneCount });
  }
  return map;
}

export interface EventBox {
  laneIndex: number;
  laneCount: number;
  /** pixel top, already cascaded so it does not overlap prior lane siblings */
  top: number;
  /** pixel height, respecting minHeightPx floor */
  height: number;
}

export interface BuildEventBoxesOptions {
  /** pixels per hour (e.g. WEEK_HOUR_HEIGHT = 48, HOUR_HEIGHT = 64) */
  pxPerHour: number;
  /** floor for visible pill height so 5-min pills remain readable */
  minHeightPx: number;
}

/**
 * Produce final pixel boxes for a day's events.
 *
 * Key idea: collisions are resolved at the PIXEL level, not at the
 * minute-level. Two events collide when their rendered rectangles
 * overlap, which includes the case where a short event's natural
 * height is clamped up to `minHeightPx` and bleeds past the next
 * event's natural top. A minute-level layout misses these collisions
 * and produces visually-overlapping pills; an earlier version tried to
 * compensate by cascading `top` within a lane, which pushed
 * back-to-back pills past the hour boundary.
 *
 * Algorithm:
 *   1. For each event compute natural `top` and clamped `height`.
 *   2. Walk events chronologically (start asc, then end desc so longer
 *      events open lanes first).
 *   3. Cluster events by pixel overlap: a new cluster starts when the
 *      current event's `top` >= the running max `bottom` of the open
 *      cluster.
 *   4. Within a cluster assign each event to the lowest-indexed lane
 *      whose last bottom is <= this event's `top`. Lane reuse is OK
 *      WITHIN a cluster because the pixel gap guarantees no visual
 *      collision. (This keeps the `exercise` trailing pill in lane 0
 *      when the brush/shower/pray trio above it has finished.)
 *   5. Stamp every event in the cluster with the cluster's final
 *      `laneCount` so column widths align horizontally.
 *
 * Motivating case: brush 06:00-06:05, shower 06:05-06:15, pray
 * 06:15-06:25, exercise 06:25-07:00 at 48px/hour with 16px floor ->
 * 3 lanes, all pills start at their natural top, exercise reuses lane 0
 * below brush, none of them cross the 07:00 line.
 */
export function buildEventBoxes<T extends LayoutInput>(
  events: readonly T[],
  opts: BuildEventBoxesOptions,
): Map<T["id"], EventBox> {
  const { pxPerHour, minHeightPx } = opts;
  if (events.length === 0) return new Map();

  const sorted = [...events].sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    return b.endMin - a.endMin;
  });

  interface Working {
    id: T["id"];
    top: number;
    height: number;
    bottom: number;
    laneIndex: number;
    clusterId: number;
  }

  const working: Working[] = [];
  const clusterLaneCount: number[] = [];
  let clusterId = -1;
  let clusterMaxBottom = -Infinity;
  let laneBottoms: number[] = [];

  for (const ev of sorted) {
    const top = (ev.startMin / 60) * pxPerHour;
    const naturalHeight = ((ev.endMin - ev.startMin) / 60) * pxPerHour;
    const height = Math.max(naturalHeight, minHeightPx);
    const bottom = top + height;

    if (top >= clusterMaxBottom) {
      if (clusterId >= 0) clusterLaneCount.push(laneBottoms.length);
      clusterId += 1;
      clusterMaxBottom = bottom;
      laneBottoms = [];
    } else {
      clusterMaxBottom = Math.max(clusterMaxBottom, bottom);
    }

    // Within a pixel-collision cluster we always open a fresh lane.
    // This keeps the "N colliding events -> N side-by-side columns"
    // mental model intact, which is what users expect: 4 habits in
    // an hour should read as 4 columns, not as 3 columns with one
    // trailing the first. Lane reuse still happens implicitly because
    // non-colliding events simply end up in a NEW cluster (see the
    // cluster-boundary check above), which resets `laneBottoms`.
    const laneIndex = laneBottoms.length;
    laneBottoms.push(bottom);

    working.push({
      id: ev.id as T["id"],
      top,
      height,
      bottom,
      laneIndex,
      clusterId,
    });
  }
  if (clusterId >= 0) clusterLaneCount.push(laneBottoms.length);

  const out = new Map<T["id"], EventBox>();
  for (const w of working) {
    out.set(w.id, {
      laneIndex: w.laneIndex,
      laneCount: clusterLaneCount[w.clusterId],
      top: w.top,
      height: w.height,
    });
  }
  return out;
}
